import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  getAllDevelopers,
  inviteDeveloper,
  getDeveloperInvitations,
  cancelDeveloperInvitation,
  resendDeveloperInvitation
} from '../api/developerManagementApi'

export default function AdminDeveloperManagement() {
  const [showInviteForm, setShowInviteForm] = useState(false)
  const [activeTab, setActiveTab] = useState('developers')
  const [inviteFormData, setInviteFormData] = useState({
    email: '',
    fullName: '',
    companyName: '',
    phone: ''
  })
  const queryClient = useQueryClient()

  const { data: developers, isLoading: developersLoading } = useQuery({
    queryKey: ['admin-developers'],
    queryFn: async () => {
      const { data, error } = await getAllDevelopers()
      if (error) throw error
      return data
    }
  })

  const { data: invitations, isLoading: invitationsLoading } = useQuery({
    queryKey: ['developer-invitations'],
    queryFn: async () => {
      const { data, error } = await getDeveloperInvitations()
      if (error) throw error
      return data
    }
  })

  const inviteMutation = useMutation({
    mutationFn: inviteDeveloper,
    onSuccess: () => {
      queryClient.invalidateQueries(['developer-invitations'])
      setShowInviteForm(false)
      setInviteFormData({ email: '', fullName: '', companyName: '', phone: '' })
      alert('Developer invitation sent successfully!')
    },
    onError: (error) => {
      alert(error.message || 'Failed to send invitation')
    }
  })

  const cancelMutation = useMutation({
    mutationFn: cancelDeveloperInvitation,
    onSuccess: () => {
      queryClient.invalidateQueries(['developer-invitations'])
      alert('Invitation cancelled successfully')
    }
  })

  const resendMutation = useMutation({
    mutationFn: resendDeveloperInvitation,
    onSuccess: () => {
      queryClient.invalidateQueries(['developer-invitations'])
      alert('Invitation resent successfully')
    }
  })

  const handleInviteSubmit = (e) => {
    e.preventDefault()
    inviteMutation.mutate(inviteFormData)
  }

  const formatDate = (date) => {
    return new Date(date).toLocaleString()
  }

  const getStatusBadge = (status) => {
    const styles = {
      pending: 'bg-yellow-100 text-yellow-800',
      accepted: 'bg-green-100 text-green-800',
      expired: 'bg-gray-100 text-gray-800',
      cancelled: 'bg-red-100 text-red-800'
    }
    return (
      <span className={`px-2 py-1 text-xs font-semibold rounded ${styles[status] || styles.pending}`}>
        {status}
      </span>
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-secondary-900">Developer Management</h2>
        <button
          onClick={() => setShowInviteForm(!showInviteForm)}
          className="btn-primary"
        >
          {showInviteForm ? 'Cancel' : 'Invite Developer'}
        </button>
      </div>

      {showInviteForm && (
        <div className="bg-white rounded-lg shadow-sm border border-secondary-200 p-6">
          <h3 className="text-lg font-semibold text-secondary-900 mb-4">Invite New Developer</h3>
          <form onSubmit={handleInviteSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-secondary-700 mb-1">
                  Email <span className="text-error-600">*</span>
                </label>
                <input
                  type="email"
                  required
                  value={inviteFormData.email}
                  onChange={(e) => setInviteFormData({ ...inviteFormData, email: e.target.value })}
                  className="input-field"
                  placeholder="developer@example.com"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-secondary-700 mb-1">
                  Full Name <span className="text-error-600">*</span>
                </label>
                <input
                  type="text"
                  required
                  value={inviteFormData.fullName}
                  onChange={(e) => setInviteFormData({ ...inviteFormData, fullName: e.target.value })}
                  className="input-field"
                  placeholder="John Smith"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-secondary-700 mb-1">
                  Company Name
                </label>
                <input
                  type="text"
                  value={inviteFormData.companyName}
                  onChange={(e) => setInviteFormData({ ...inviteFormData, companyName: e.target.value })}
                  className="input-field"
                  placeholder="ABC Developments Ltd"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-secondary-700 mb-1">
                  Phone
                </label>
                <input
                  type="tel"
                  value={inviteFormData.phone}
                  onChange={(e) => setInviteFormData({ ...inviteFormData, phone: e.target.value })}
                  className="input-field"
                  placeholder="+64 21 123 4567"
                />
              </div>
            </div>
            <div className="flex justify-end space-x-3">
              <button
                type="button"
                onClick={() => setShowInviteForm(false)}
                className="btn-outline"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={inviteMutation.isPending}
                className="btn-primary"
              >
                {inviteMutation.isPending ? 'Sending...' : 'Send Invitation'}
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="bg-white rounded-lg shadow-sm border border-secondary-200">
        <div className="border-b border-secondary-200">
          <nav className="flex space-x-8 px-6" aria-label="Tabs">
            <button
              onClick={() => setActiveTab('developers')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'developers'
                  ? 'border-primary-600 text-primary-600'
                  : 'border-transparent text-secondary-500 hover:text-secondary-700 hover:border-secondary-300'
              }`}
            >
              Active Developers ({developers?.length || 0})
            </button>
            <button
              onClick={() => setActiveTab('invitations')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'invitations'
                  ? 'border-primary-600 text-primary-600'
                  : 'border-transparent text-secondary-500 hover:text-secondary-700 hover:border-secondary-300'
              }`}
            >
              Invitations ({invitations?.length || 0})
            </button>
          </nav>
        </div>

        <div className="p-6">
          {activeTab === 'developers' && (
            <div>
              {developersLoading ? (
                <div className="text-center py-8 text-secondary-500">Loading developers...</div>
              ) : !developers || developers.length === 0 ? (
                <div className="text-center py-8 text-secondary-500">
                  No developers yet. Invite your first developer to get started.
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-secondary-200">
                    <thead>
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-secondary-500 uppercase tracking-wider">
                          Developer
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-secondary-500 uppercase tracking-wider">
                          Company
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-secondary-500 uppercase tracking-wider">
                          Contact
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-secondary-500 uppercase tracking-wider">
                          Joined
                        </th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-secondary-500 uppercase tracking-wider">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-secondary-200">
                      {developers.map((dev) => (
                        <tr key={dev.id} className="hover:bg-secondary-50">
                          <td className="px-4 py-4 whitespace-nowrap">
                            <div className="text-sm font-medium text-secondary-900">{dev.full_name}</div>
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap">
                            <div className="text-sm text-secondary-500">{dev.company_name || '-'}</div>
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap">
                            <div className="text-sm text-secondary-500">{dev.email}</div>
                            {dev.phone && <div className="text-xs text-secondary-400">{dev.phone}</div>}
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap text-sm text-secondary-500">
                            {new Date(dev.created_at).toLocaleDateString()}
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap text-right text-sm font-medium">
                            <a
                              href={`/admin/developers/${dev.id}`}
                              className="text-primary-600 hover:text-primary-900"
                            >
                              View Details
                            </a>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {activeTab === 'invitations' && (
            <div>
              {invitationsLoading ? (
                <div className="text-center py-8 text-secondary-500">Loading invitations...</div>
              ) : !invitations || invitations.length === 0 ? (
                <div className="text-center py-8 text-secondary-500">
                  No invitations sent yet.
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-secondary-200">
                    <thead>
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-secondary-500 uppercase tracking-wider">
                          Email
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-secondary-500 uppercase tracking-wider">
                          Status
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-secondary-500 uppercase tracking-wider">
                          Invited By
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-secondary-500 uppercase tracking-wider">
                          Created
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-secondary-500 uppercase tracking-wider">
                          Expires
                        </th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-secondary-500 uppercase tracking-wider">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-secondary-200">
                      {invitations.map((inv) => (
                        <tr key={inv.id} className="hover:bg-secondary-50">
                          <td className="px-4 py-4 whitespace-nowrap">
                            <div className="text-sm font-medium text-secondary-900">{inv.email}</div>
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap">
                            {getStatusBadge(inv.status)}
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap text-sm text-secondary-500">
                            {inv.invited_by_profile?.full_name || 'Unknown'}
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap text-sm text-secondary-500">
                            {formatDate(inv.created_at)}
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap text-sm text-secondary-500">
                            {formatDate(inv.expires_at)}
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap text-right text-sm font-medium">
                            {inv.status === 'pending' && (
                              <div className="flex justify-end space-x-2">
                                <button
                                  onClick={() => resendMutation.mutate(inv.id)}
                                  disabled={resendMutation.isPending}
                                  className="text-primary-600 hover:text-primary-900"
                                >
                                  Resend
                                </button>
                                <button
                                  onClick={() => cancelMutation.mutate(inv.id)}
                                  disabled={cancelMutation.isPending}
                                  className="text-error-600 hover:text-error-900"
                                >
                                  Cancel
                                </button>
                              </div>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
